import {
  Anchor,
  Box,
  Button,
  Group,
  List,
  SimpleGrid,
  Stack,
} from '@mantine/core';
import { createFileRoute,Link } from '@tanstack/react-router';

import { IconMail, IconMusic, IconPerson, IconPlace, IconRoad } from '../components/brand';
import { MarketingHero } from '../components/marketing/MarketingHero';
import { MarketingNextSteps } from '../components/marketing/MarketingNextSteps';
import { MarketingSection } from '../components/marketing/MarketingSection';
import { PublicShell } from '../components/marketing/PublicShell';
import { SmartRiderSection } from '../components/marketing/SmartRiderSection';
import { JoinLink } from '../components/ui/JoinLink';
import { StepCard } from '../components/ui/StepCard';
import { useCmsImage, useCmsPage, useCmsSlot } from '../lib/cms';
import { CTA_CLAY } from '../lib/cta-clay';

export const Route = createFileRoute('/how-it-works')({
  component: HowItWorksPage,
});

/**
 * How many step positions have a declared screenshot slot per flow. Both flows
 * currently hold six steps; a seventh added in the editor renders without a
 * screenshot rather than throwing on an undeclared slot key (useCmsPage throws
 * on unknown keys, by design).
 */
const FLOW_SHOT_POSITIONS = 6;

interface FlowStep {
  readonly n: string;
  readonly title: string;
  readonly body: string;
  readonly footnote?: string;
  /** Screenshot of the product screen this step describes, if there is one. */
  readonly shot?: { readonly url: string; readonly alt: string };
}

function HowItWorksPage() {
  // CMS-editable flow steps (admin → /admin/settings → CMS → How it works).
  // The step number is positional, so it stays correct as steps are added or
  // reordered in the editor.
  //
  // Each step's screenshot lives in its own image slot keyed by POSITION
  // (`how_it_works.<flow>_step_<n>_image`) rather than inside the StepItem,
  // because StepItem has no image field and adding one would mean a new slot
  // kind plus an editor rewrite for this one page. The consequence — reordering
  // steps re-pairs the screenshots — is called out in each slot's help text.
  // Resolved through useCmsPage so the number of image lookups can follow the
  // number of steps without putting a hook in a loop.
  const cms = useCmsPage('how_it_works');
  const buildFlow = (flow: 'artist' | 'host'): ReadonlyArray<FlowStep> =>
    cms.get<'step_list'>(`how_it_works.${flow}_steps`).map((s, i) => {
      const n = i + 1;
      // Only the six declared positions have a slot; extra steps added in the
      // editor simply render without a screenshot rather than throwing.
      const shot = n <= FLOW_SHOT_POSITIONS
        ? cms.get<'image'>(`how_it_works.${flow}_step_${n}_image`)
        : undefined;
      return {
        n: String(n),
        title: s.title,
        body: s.body,
        ...(s.footnote !== undefined ? { footnote: s.footnote } : {}),
        ...(cms.ready && shot !== undefined && shot.url !== ''
          ? { shot: { url: shot.url, alt: shot.alt } }
          : {}),
      };
    });

  const artistSteps = buildFlow('artist');
  const hostSteps = buildFlow('host');

  const { image: heroImg, ready: heroImgReady } = useCmsImage('how_it_works.hero_image');
  const heroEyebrow = useCmsSlot<'markdown'>('how_it_works.hero_eyebrow');
  const heroTitle = useCmsSlot<'markdown'>('how_it_works.hero_title');
  const heroLede = useCmsSlot<'markdown'>('how_it_works.hero_lede');
  const artistEyebrow = useCmsSlot<'markdown'>('how_it_works.artist_eyebrow');
  const artistTitle = useCmsSlot<'markdown'>('how_it_works.artist_title');
  const hostEyebrow = useCmsSlot<'markdown'>('how_it_works.host_eyebrow');
  const hostTitle = useCmsSlot<'markdown'>('how_it_works.host_title');
  const finePrintEyebrow = useCmsSlot<'markdown'>('how_it_works.fine_print_eyebrow');
  const finePrintTitle = useCmsSlot<'markdown'>('how_it_works.fine_print_title');
  const finePrintItems = useCmsSlot<'bullet_list'>('how_it_works.fine_print_items');
  const artistCtaLabel = useCmsSlot<'markdown'>('how_it_works.artist_cta_label');
  const hostCtaLabel = useCmsSlot<'markdown'>('how_it_works.host_cta_label');
  const finePrintCtaPrimary = useCmsSlot<'markdown'>('how_it_works.fine_print_cta_primary');
  const finePrintCtaSecondary = useCmsSlot<'markdown'>('how_it_works.fine_print_cta_secondary');
  const nextStepsTitle = useCmsSlot<'markdown'>('how_it_works.next_steps_title');

  return (
    <PublicShell>
      <MarketingHero
        eyebrow={heroEyebrow}
        title={heroTitle}
        lede={heroLede}
        {...(heroImgReady ? { backgroundImage: heroImg.url } : {})}
        backgroundImageAlt={heroImg.alt}
        backgroundImageOpacity={heroImg.opacity ?? 0.2}
      />

      {/* The two flow sections run wider than the rest of the page (lg, not the
          default md) because they are the only sections carrying a card grid
          with screenshots in it — at md each shot was a third of a column and
          read as a thumbnail. The prose sections below stay md so the reading
          measure doesn't stretch. */}
      <MarketingSection eyebrow={artistEyebrow} title={artistTitle} size="lg">
        <Stack gap="xl">
          <FlowGrid steps={artistSteps} />
          {/* A sign-up nudge sits right after each flow so the reader can act at
              the moment the path clicks, not only at the bottom of the page. */}
          <SignUpCta role="artist" label={artistCtaLabel} />
        </Stack>
      </MarketingSection>

      <MarketingSection eyebrow={hostEyebrow} title={hostTitle} tinted size="lg">
        <Stack gap="xl">
          <FlowGrid steps={hostSteps} />
          <SignUpCta role="host" label={hostCtaLabel} />
        </Stack>
      </MarketingSection>

      {/* The Smart Rider. Moved off the homepage (Richie, feedback DYFqwfvei8) —
          it needed the two flows above it to make sense of "generated from what
          you already filled in". */}
      <SmartRiderSection />

      <MarketingSection eyebrow={finePrintEyebrow} title={finePrintTitle}>
        <List spacing="md" size="lg" style={{ lineHeight: 1.6 }}>
          {finePrintItems.map((item) => (
            <List.Item key={item}>{item}</List.Item>
          ))}
        </List>

        <Group mt="xl" gap="md">
          <JoinLink>
            {/* Solid Clay, matching the homepage artist CTAs (2026-08-18). The
                Clay-to-Sun gradient this used to be is now the odd one out: a
                visitor arriving from the homepage would meet the same button in
                two treatments one click apart. `brand.6` IS #d2602e, but the
                token is used so admin branding still flows through, and the
                label colour is left to Mantine's autoContrast. */}
            <Button
              component="span"
              tabIndex={-1}
              size="lg"
              variant="filled"
              color={CTA_CLAY}
            >
              {finePrintCtaPrimary}
            </Button>
          </JoinLink>
          <Anchor component={Link} to="/our-ethos" c="brand.7" fw={500}>
            {finePrintCtaSecondary}
          </Anchor>
        </Group>
      </MarketingSection>

      <MarketingNextSteps
        title={nextStepsTitle}
        destinations={['/our-ethos', '/founding-venues', '/faq']}
      />

    </PublicShell>
  );
}

/**
 * SignUpCta — a role-aware sign-up button placed after each flow. Uses the
 * Link-wrapping-Button pattern so the typed `?role=` search survives Mantine's
 * polymorphic-as-Link generic erasure (see CLAUDE.md UI gotchas).
 *
 * Colour carries the audience: Clay (the spark) for artists, Tide (the water)
 * for venues. Same size and radius either way, so the two section CTAs stay
 * siblings at a glance.
 *
 * The two halves are no longer the same treatment, and that is deliberate. The
 * artist half is solid Clay so it matches the homepage artist CTAs, which moved
 * off the Clay-to-Sun gradient on 2026-08-18; the venue half keeps the Tide
 * gradient the homepage venue CTAs still carry. The rule is "match whatever the
 * homepage does for this audience", so if the venue CTAs are ever flattened
 * too, flatten this one with them rather than leaving it as the odd one out.
 */
function SignUpCta({ role, label }: { role: 'artist' | 'host'; label: string }) {
  return (
    <Group gap="md">
      <JoinLink audience={role}>
        {role === 'host' ? (
          <Button
            component="span"
            tabIndex={-1}
            size="md"
            variant="gradient"
            gradient={{ from: 'tide.7', to: 'tide.6', deg: 35 }}
          >
            {label} →
          </Button>
        ) : (
          <Button component="span" tabIndex={-1} size="md" variant="filled" color={CTA_CLAY}>
            {label} →
          </Button>
        )}
      </JoinLink>
    </Group>
  );
}

/**
 * FlowGrid — the flow's steps as a simple two-up card grid.
 *
 * Restored 2026-08-18 (Richie, feedback HpoZ1F0of6m5XKnkdZ99t: "too much
 * scrolling, the text positioning and big graphic cards aren't working for me.
 * Please can we go back to the simpler card layout for launch?"). The journey
 * timeline it replaces is parked in `components/marketing/FlowTimeline.tsx` —
 * left in place, not deleted, because he wants to come back to it once email
 * capture is live.
 *
 * The screenshots stay: the objection was to the big graphic cards, not to
 * having pictures. Each shot rides along at card scale through StepCard's
 * `media` prop.
 */
function FlowGrid({ steps }: { steps: ReadonlyArray<FlowStep> }) {
  return (
    <SimpleGrid cols={{ base: 1, md: 2 }} spacing={{ base: 'md', md: 'lg' }}>
      {steps.map((step) => (
        <FlowStepCard key={step.n} step={step} />
      ))}
    </SimpleGrid>
  );
}

// Distinct per-step glyphs, cycled by position, so each card carries its own
// symbol like the homepage How-it-works cards — instead of the repeated ripple
// mark, which read as a wifi symbol (feedback ph6pk / y01zv). Deliberately
// excludes IconRipple for that reason.
const FLOW_ICONS = [IconPerson, IconPlace, IconMail, IconRoad, IconMusic] as const;

function FlowStepCard({ step }: { step: FlowStep }) {
  // Shared StepCard treatment — the same card the homepage How it works uses, so
  // the two surfaces read as one system. 20px glyph to match StepCard's light
  // tone tile, which is now 38px (Richie asked for the smaller dark icons from
  // the Inside wwaave callouts — see StepCard's header note).
  const Icon = FLOW_ICONS[(Number(step.n) - 1) % FLOW_ICONS.length] ?? IconPerson;
  return (
    <StepCard
      tone="light"
      icon={<Icon size={20} />}
      label={`STEP ${step.n.padStart(2, '0')}`}
      title={step.title}
      body={step.body}
      footnote={step.footnote}
      {...(step.shot !== undefined ? { media: <StepShot shot={step.shot} /> } : {})}
    />
  );
}

/**
 * A step's image, when there is one.
 *
 * Blank by default site-wide: the interface-fragment captures that used to sit
 * here were retired on 2026-08-18 and the page is plain cards until someone
 * uploads an image to `how_it_works.<flow>_step_<n>_image`. Whatever they
 * upload renders at NATURAL height — no fixed aspect box — so an image is never
 * cropped to a shape it was not made for and never letterboxed into one.
 *
 * Decorative, and deliberately so: the step's own title and body already name
 * the feature, so an alt would be the third telling.
 */
function StepShot({ shot }: { shot: { url: string; alt: string } }) {
  return (
    <Box
      component="img"
      src={shot.url}
      alt={shot.alt}
      aria-hidden={shot.alt === '' ? true : undefined}
      loading="lazy"
      decoding="async"
      style={{
        display: 'block',
        width: '100%',
        height: 'auto',
        borderBottom: '1px solid var(--mantine-color-ink-2)',
        backgroundColor: 'var(--mantine-color-ink-0)',
      }}
    />
  );
}
